import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorForEndUser } from '@app/core/utils/error-for-end-user.error';
import { ErrorResponse } from '@coreModels/error-response';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/browser';
import { Replay } from '@sentry/replay';
import { TokenStorageService } from '@services/token-storage.service';
import { codeVersion } from '@services/utils/global-config';
import { sentryDsn } from './utils/global-config';
import { TranslateService } from '@ngx-translate/core';

interface StoreFailureException {
  type: string;
  payload: ErrorResponse;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  error: Error | HttpErrorResponse | StoreFailureException | ErrorResponse;
  initExecuted = false;

  CHUNK_FAILED_MESSAGE = /Loading chunk [\d]+ failed/;
  MULTIPLE_SESSIONS = /Multiple Sentry Session Replay instances are not supported/;
  WRONG_HTTP_REDIRECTION = /Http failure response for [\S]+: 0 Unknown Error/;
  PARSE_ERROR_MESSAGE = 'Http failure during parsing for';
  FIREBASE_ERROR_MESSAGE =
    "Messaging: This browser doesn't support the API's required to use the firebase SDK. (messaging/unsupported-browser).";

  STATUS_UNKNOWN = 0;
  STATUS_REDIRECTION_MOVED = 301;
  STATUS_REDIRECTION_FOUND = 302;
  STATUS_VALIDATIONS = 400;
  STATUS_UNAUTHENTICATED = 401;
  STATUS_FORBIDDEN = 403;
  STATUS_TIMEOUT = 408;
  STATUS_SERVER = 500;
  STATUS_UNAVAILABLE = 503;

  constructor(
    private injector: Injector,
    private ngZone: NgZone,
    private tokenStorage: TokenStorageService,
    private _translateService: TranslateService,
  ) {}

  handleHttpErrorResponse(error: HttpErrorResponse | ErrorResponse) {
    this.error = error;

    if (this.handleServerSideError(error)) {
      return;
    }

    if (this.isValidation(error)) {
      return this.throwError();
    }

    if (this.needLogout(error)) {
      this.ngZone.run(() => {
        const router = this.injector.get(Router);
        router.navigate(['auth/logout']);
      });
      return false;
    }

    if (this.shouldLogout(error)) {
      localStorage.removeItem('user');
    }

    if (this.needReload(error)) {
      window.location.reload();
      return this.throwError();
    }

    if (error.status === this.STATUS_SERVER) {
      return this.throwError();
    }

    this.addSentryLog();
  }

  private handleServerSideError(error: any): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    if (
      [this.STATUS_UNAUTHENTICATED, this.STATUS_FORBIDDEN, this.STATUS_UNKNOWN, this.STATUS_UNAVAILABLE].includes(
        error.status,
      )
    ) {
      return false;
    }

    if (!environment.sentryLogging) {
      this.throwError();
      return false;
    }

    this.initializeSentry();

    const url = error.url || 'Unknown URL';
    const status = error.status || 'Unknown Status';
    const message = error.message || 'No message';

    const formattedTitle = `HTTP ${status} at ${url}`;

    Sentry.withScope((scope) => {
      scope.setUser({ username: this.tokenStorage.getUser('name') });

      scope.setExtra('API Response Body', error.error);
      scope.setTag('status_code', status.toString());
      scope.setTag('error-type', 'Server API Error');
      scope.setTransactionName(formattedTitle);

      Sentry.captureException(new Error(`${formattedTitle} - ${message}`));
    });

    return true;
  }

  private initializeSentry() {
    if (!this.initExecuted) {
      const sentryVersion = `${environment.sentryServerName}@${codeVersion}`;
      const sentryReplayOptions = {
        maskAllText: false,
        blockAllMedia: false,
      };

      Sentry.init({
        dsn: sentryDsn,
        release: sentryVersion,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1,
        ignoreErrors: ['Non-Error exception captured'],
        integrations: [new Replay(sentryReplayOptions)],
      });
      this.initExecuted = true;
    }
  }

  addSentryLog(errorMessage?: string, title?: string) {
    if (!environment.sentryLogging) {
      return;
    }

    if (this.MULTIPLE_SESSIONS.test(this.error?.message)) return;
    if (this.error?.message === this.FIREBASE_ERROR_MESSAGE) return;
    if (this.error instanceof ErrorForEndUser) return;

    this.initializeSentry();

    Sentry.configureScope((scope) => {
      scope.setUser({ username: this.tokenStorage.getUser('name') });

      if (title) {
        scope.setTransactionName(title);
      }

      if (errorMessage) {
        scope.setTag('error-type', 'UI Warning');
        return;
      }

      if (!this.isServerError()) {
        scope.setTag('error-type', 'UI Error');
        return;
      }

      if (this.serverReturnsHtml()) {
        scope.setTag('error-type', 'Server Returns Html');
        return;
      }

      scope.setTag(
        'error-type',
        `${this._translateService.instant('snackbar.danger.serverError')} ${(this.error as HttpErrorResponse).status}`,
      );
      scope.setTag('server-name', `${environment.sentryServerName}`);
    });

    if (errorMessage) {
      Sentry.captureException(new Error(errorMessage));
    } else if (this.error instanceof Error) {
      Sentry.captureException(this.error);
    } else {
      const httpError = this.error as HttpErrorResponse;
      const err = new Error(
        httpError?.status
          ? `HTTP ${httpError.status}: ${httpError.message || 'Unknown error'}`
          : this.error?.message || 'Unknown error',
      );
      err.name = (this.error as any)?.name || 'HttpError';
      Sentry.captureException(err);
    }
  }

  shouldLogout(error): boolean {
    return error.status === this.STATUS_UNAVAILABLE;
  }

  needLogout(error: HttpErrorResponse | ErrorResponse) {
    return (
      [this.STATUS_UNAUTHENTICATED].includes(error.status) ||
      error.message.indexOf(this.PARSE_ERROR_MESSAGE) > -1 ||
      this.WRONG_HTTP_REDIRECTION.test(error.message)
    );
  }

  needReload(error: HttpErrorResponse | ErrorResponse) {
    return (
      !navigator.onLine ||
      this.CHUNK_FAILED_MESSAGE.test(error.message) ||
      [
        this.STATUS_UNKNOWN,
        this.STATUS_UNAVAILABLE,
        this.STATUS_REDIRECTION_MOVED,
        this.STATUS_REDIRECTION_FOUND,
        this.STATUS_TIMEOUT,
      ].includes(error.status)
    );
  }

  throwError() {
    if (!environment.production) {
      const error = JSON.parse(JSON.stringify(this.error));
      console.error(this.error);
    }
  }

  isServerError() {
    return this.error instanceof HttpErrorResponse;
  }

  isStoreException(error) {
    return error && error.hasOwnProperty('payload') && error.payload instanceof ErrorResponse;
  }

  isValidation(error: HttpErrorResponse | ErrorResponse) {
    return error.status === this.STATUS_VALIDATIONS;
  }

  serverReturnsHtml() {
    try {
      const appName = environment.appName;
      const isAppNameInTitle = (this.error as HttpErrorResponse).error.text.indexOf(`<title>${appName}</title>`) > 0;
      return isAppNameInTitle;
    } catch (e) {
      return false;
    }
  }
}
